import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  calculateDiscountedPrice,
  formatDiscountPreview,
  formatTicketPrice,
  loadReferralLinks,
  saveReferralLinks,
  suggestRefCodeFromLabel,
  type ReferralLinkDraft,
  type TicketFormSlice,
} from './paymentFormUtils';
import { useBrand } from '../../../brands/BrandProvider';

interface PromotionalLinkWizardProps {
  mode: 'legacy' | 'batched';
  slug: string;
  onLinkCreated?: () => void;
}

interface BatchedTypeSlice {
  name?: string;
  hiddenFromPublicPage?: boolean;
  isActive?: boolean;
  batches?: Array<{
    price?: number;
    _id?: string;
    quantity?: number;
    startSaleDate?: Date | string;
    endSaleDate?: Date | string;
  }>;
}

type LegacyPublicOption = { mode: 'legacy'; ticket: TicketFormSlice; index: number };
type BatchedPublicOption = { mode: 'batched'; type: BatchedTypeSlice; index: number };
type PublicOption = LegacyPublicOption | BatchedPublicOption;

const PromotionalLinkWizard: React.FC<PromotionalLinkWizardProps> = ({ mode, slug, onLinkCreated }) => {
  const brand = useBrand();
  const { watch, setValue, control } = useFormContext();
  const { append: appendTicket } = useFieldArray({ control, name: 'tickets' });
  const { append: appendType } = useFieldArray({ control, name: 'ticketTypes' });

  const tickets: TicketFormSlice[] = watch('tickets') || [];
  const ticketTypes: BatchedTypeSlice[] = watch('ticketTypes') || [];
  const ticketLinks = watch('ticketLinks') || [];

  const [label, setLabel] = useState('');
  const [baseKey, setBaseKey] = useState('');
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discountPercent, setDiscountPercent] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const publicOptions = useMemo((): PublicOption[] => {
    if (mode === 'legacy') {
      return tickets
        .map((ticket, index) => ({ mode: 'legacy' as const, ticket, index }))
        .filter(({ ticket }) => ticket.isActive !== false && !ticket.hiddenFromPublicPage);
    }
    return ticketTypes
      .map((type, index) => ({ mode: 'batched' as const, type, index }))
      .filter(({ type }) => type.isActive !== false && !type.hiddenFromPublicPage);
  }, [mode, tickets, ticketTypes]);

  const selectedBase = useMemo(() => {
    if (!baseKey) return null;
    if (mode === 'legacy') {
      const index = Number(baseKey);
      const ticket = tickets[index];
      if (!ticket) return null;
      return {
        index,
        name: ticket.name || `Ingresso ${index + 1}`,
        price: Number(ticket.price) || 0,
        ticketId: ticket._id,
      };
    }
    const index = Number(baseKey);
    const type = ticketTypes[index];
    if (!type) return null;
    const batch = type.batches?.[0];
    return {
      index,
      name: type.name || `Ingresso ${index + 1}`,
      price: Number(batch?.price) || 0,
      batch,
      batchId: batch?._id,
    };
  }, [baseKey, mode, tickets, ticketTypes]);

  const discountNum = hasDiscount ? Math.min(100, Math.max(0, Number(discountPercent) || 0)) : 0;
  const finalPrice =
    selectedBase && hasDiscount && discountNum > 0
      ? calculateDiscountedPrice(selectedBase.price, discountNum)
      : selectedBase?.price ?? 0;


  const persistLegacyReferral = (entry: ReferralLinkDraft) => {
    if (!slug) return;
    const existing = loadReferralLinks(slug);
    saveReferralLinks(slug, [...existing, entry]);
  };

  const handleCreate = () => {
    setFeedback(null);

    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setFeedback({ type: 'error', text: 'Informe um nome para identificar este link (ex.: WhatsApp do grupo).' });
      return;
    }
    if (!selectedBase || baseKey === '') {
      setFeedback({ type: 'error', text: 'Selecione qual ingresso este link vai usar.' });
      return;
    }
    if (hasDiscount && selectedBase.price <= 0) {
      setFeedback({ type: 'error', text: 'Ingresso gratuito não aceita desconto — crie o link sem marcar a opção de desconto.' });
      return;
    }
    if (hasDiscount) {
      if (discountNum <= 0) {
        setFeedback({ type: 'error', text: 'Informe a porcentagem de desconto.' });
        return;
      }
      if (discountNum >= 100) {
        setFeedback({ type: 'error', text: 'O desconto deve ser menor que 100%.' });
        return;
      }
      if (finalPrice <= 0) {
        setFeedback({ type: 'error', text: 'O preço com desconto ficou inválido.' });
        return;
      }
    }

    const refCode = suggestRefCodeFromLabel(trimmedLabel);
    const linkId = `link-${Date.now()}`;

    if (hasDiscount) {
      if (mode === 'legacy') {
        const baseIndex = Number(baseKey);
        const base = tickets[baseIndex];
        const newIndex = tickets.length;

        appendTicket({
          name: `${base.name} — ${trimmedLabel}`,
          description: `Desconto de ${discountNum}% exclusivo para quem acessar pelo link "${trimmedLabel}".`,
          price: finalPrice,
          quantity: base.quantity ?? 50,
          startSaleDate: base.startSaleDate ?? new Date(),
          endSaleDate: base.endSaleDate ?? new Date(new Date().setDate(new Date().getDate() + 30)),
          isRestricted: false,
          allowedEmails: [],
          isActive: true,
          hiddenFromPublicPage: true,
        });

        persistLegacyReferral({
          id: linkId,
          label: trimmedLabel,
          refCode,
          ticketIndex: newIndex,
          hasDiscount: true,
          discountPercent: discountNum,
          finalPrice,
          baseTicketName: selectedBase.name,
          usesHiddenTicket: true,
          baseTicketIndex: baseIndex,
        });

      } else {
        const baseIndex = Number(baseKey);
        const baseType = ticketTypes[baseIndex];
        const baseBatch = baseType.batches?.[0];
        const newTypeIndex = ticketTypes.length;

        appendType({
          name: `${baseType.name} — ${trimmedLabel}`,
          description: `Desconto de ${discountNum}% via link "${trimmedLabel}". Não aparece na página do evento.`,
          hiddenFromPublicPage: true,
          isRestricted: false,
          allowedEmails: [],
          isActive: true,
          sortOrder: newTypeIndex,
          batches: [
            {
              name: trimmedLabel,
              price: finalPrice,
              quantity: baseBatch?.quantity ?? 100,
              startSaleDate: baseBatch?.startSaleDate ?? new Date(),
              endSaleDate: baseBatch?.endSaleDate ?? new Date(new Date().setDate(new Date().getDate() + 30)),
              sortOrder: 0,
              isActive: true,
              autoAdvanceWhen: 'date',
              linkOnly: true,
            },
          ],
        });

        setValue(
          'ticketLinks',
          [
            ...ticketLinks,
            {
              label: trimmedLabel,
              refCode,
              ticketTypeId: null,
              ticketBatchId: null,
              isActive: true,
              promoTypeIndex: newTypeIndex,
              hasDiscount: true,
              discountPercent: discountNum,
              finalPrice,
              baseTicketName: selectedBase.name,
              baseTypeIndex: baseIndex,
            },
          ],
          { shouldDirty: true }
        );

      }
    } else if (mode === 'legacy') {
      const baseIndex = Number(baseKey);

      persistLegacyReferral({
        id: linkId,
        label: trimmedLabel,
        refCode,
        ticketIndex: baseIndex,
        hasDiscount: false,
        finalPrice: selectedBase.price,
        baseTicketName: selectedBase.name,
        usesHiddenTicket: false,
      });
    } else {
      const baseIndex = Number(baseKey);
      const baseBatch = ticketTypes[baseIndex]?.batches?.[0];

      setValue(
        'ticketLinks',
        [
          ...ticketLinks,
          {
            label: trimmedLabel,
            refCode,
            ticketTypeId: null,
            ticketBatchId: baseBatch?._id || null,
            isActive: true,
            hasDiscount: false,
            discountPercent: 0,
            finalPrice: selectedBase.price,
            baseTicketName: selectedBase.name,
          },
        ],
        { shouldDirty: true }
      );
    }

    const createdWithDiscount = hasDiscount;

    setLabel('');
    setHasDiscount(false);
    setDiscountPercent('');
    onLinkCreated?.();
    setFeedback({
      type: 'success',
      text: createdWithDiscount
        ? 'Link com desconto criado! Gerencie em "Seus links personalizados" abaixo (não aparece na aba Ingressos). Salve o evento.'
        : 'Link criado! Veja em "Seus links personalizados" abaixo e salve o evento.',
    });
  };

  const discountValid =
    !hasDiscount ||
    (selectedBase != null &&
      selectedBase.price > 0 &&
      discountNum > 0 &&
      discountNum < 100 &&
      finalPrice > 0);

  const canCreate =
    publicOptions.length > 0 &&
    !!label.trim() &&
    baseKey !== '' &&
    selectedBase != null &&
    discountValid;

  const showPricePreview = selectedBase != null;
  const discountPreview =
    selectedBase != null ? formatDiscountPreview(selectedBase.price, discountNum, finalPrice) : null;

  return (
    <Stack spacing={2.5}>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          bgcolor: 'grey.50',
          borderColor: 'primary.main',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkOutlinedIcon color="primary" fontSize="small" />
          Criar link de divulgação
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Gere um link para WhatsApp, Instagram ou grupos. Por padrão usa o preço normal do ingresso.
          Marque a opção abaixo se quiser aplicar desconto — o valor final aparece na hora.
          {hasDiscount && (
            <> Com desconto, o sistema reserva um preço exclusivo para esse link (não aparece na aba Ingressos).</>
          )}
        </Typography>

        <Stack spacing={2}>
          {publicOptions.length === 0 && (
            <Alert severity="info">
              Primeiro cadastre um ingresso visível na página (aba &quot;Ingressos&quot;) e defina o preço.
            </Alert>
          )}

          <TextField
            label="Para quem é este link?"
            placeholder={brand.copy.eventForm.promoLinkPlaceholder}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
            helperText="Só para você se organizar — aparece na lista de links."
          />

          <FormControl fullWidth size="small" disabled={publicOptions.length === 0}>
            <InputLabel id="promo-base-ticket">Qual ingresso?</InputLabel>
            <Select
              labelId="promo-base-ticket"
              label="Qual ingresso?"
              value={baseKey}
              onChange={(e) => setBaseKey(String(e.target.value))}
            >
              {publicOptions.map((opt) => {
                const key = String(opt.index);
                const name = opt.mode === 'legacy' ? opt.ticket.name : opt.type.name;
                const price =
                  opt.mode === 'legacy'
                    ? Number(opt.ticket.price)
                    : Number(opt.type.batches?.[0]?.price);
                return (
                  <MenuItem key={key} value={key}>
                    {name?.trim() || `Ingresso ${opt.index + 1}`} — {formatTicketPrice(price)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {showPricePreview && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quem usar este link pagará:
              </Typography>
              <Typography variant="h6" fontWeight={700} color={hasDiscount && discountNum > 0 && !discountPreview?.isFree ? 'success.main' : 'text.primary'}>
                {formatTicketPrice(finalPrice)}
              </Typography>
              {hasDiscount && discountNum > 0 && discountPreview && !discountPreview.isFree && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {discountPreview.detail}
                </Typography>
              )}
              {hasDiscount && discountPreview?.isFree && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                  Ingresso gratuito — desconto não altera o valor.
                </Typography>
              )}
              {!hasDiscount && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Preço normal do ingresso — sem desconto
                </Typography>
              )}
              {label.trim() && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Código na URL: ?ref={suggestRefCodeFromLabel(label)}
                </Typography>
              )}
            </Paper>
          )}

          {selectedBase && selectedBase.price <= 0 && !hasDiscount && (
            <Alert severity="info">
              Ingresso gratuito — quem usar o link também inscreve-se de graça.
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={hasDiscount}
                disabled={selectedBase != null && selectedBase.price <= 0}
                onChange={(e) => {
                  setHasDiscount(e.target.checked);
                  if (!e.target.checked) setDiscountPercent('');
                }}
              />
            }
            label="Aplicar desconto neste link"
          />

          {selectedBase && selectedBase.price <= 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1, ml: 4.5 }}>
              Desconto só faz sentido em ingressos pagos.
            </Typography>
          )}

          <Collapse in={hasDiscount}>
            <TextField
              label="Desconto"
              type="number"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              size="small"
              fullWidth
              sx={{ maxWidth: 200 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 1, max: 99, step: 1 }}
              helperText="Ex.: 20 = 20% de desconto. O valor final atualiza acima."
            />
          </Collapse>

          <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
            Criar link
          </Button>

          {feedback && <Alert severity={feedback.type}>{feedback.text}</Alert>}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default PromotionalLinkWizard;
